import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dbRealtime, ref, get } from '../environments/firebase';
import { jsPDF } from 'jspdf';

type Pravachan = {
  vakta: string;
  topic: string;
  dateTime: string;
  notes: string;
  images?: string[];
  createdAt?: number;
};

// Utility function to safely render HTML
const renderHTML = (htmlString: string) => {
  return { __html: htmlString };
};

export default function SharedPravachan() {
  const { userId, timestamp } = useParams();
  const [pravachan, setPravachan] = useState<Pravachan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Update meta tags for link preview
  useEffect(() => {
    const updateMetaTags = (data: Pravachan) => {
      const title = `Pravachan: ${data.topic}`;
      const desc = `Discourse by ${data.vakta} on ${new Date(data.dateTime || timestamp!).toLocaleDateString()}`;
      const image = data.images?.[0] || `${window.location.origin}/logo192.png`;
      const url = window.location.href;

      // Update OG tags
      document.querySelector('meta[property="og:title"]')?.setAttribute('content', title);
      document.querySelector('meta[property="og:description"]')?.setAttribute('content', desc);
      document.querySelector('meta[property="og:image"]')?.setAttribute('content', image);
      document.querySelector('meta[property="og:url"]')?.setAttribute('content', url);

      // Update Twitter cards
      document.querySelector('meta[name="twitter:card"]')?.setAttribute('content', 'summary_large_image');
      document.querySelector('meta[name="twitter:title"]')?.setAttribute('content', title);
      document.querySelector('meta[name="twitter:description"]')?.setAttribute('content', desc);
      document.querySelector('meta[name="twitter:image"]')?.setAttribute('content', image);
    };

    const fetchPravachan = async () => {
      try {
        const snapshot = await get(ref(dbRealtime, `publicPravachans/${userId}/${timestamp}`));
        if (snapshot.exists()) {
          const data = snapshot.val();
          setPravachan(data);
          updateMetaTags(data);
        } else {
          setError('Pravachan not found');
        }
      } catch (err) {
        console.error('Error fetching pravachan:', err);
        setError('Failed to load pravachan');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPravachan();
  }, [userId, timestamp]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const downloadPDF = async () => {
    if (!pravachan) return;
    
    const pdf = new jsPDF({
      orientation: 'p',
      unit: 'mm',
      format: 'a4',
      putOnlyUsedFonts: true,
      compress: true
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 20;
    let yPosition = 30;

    // Function to strip HTML tags but preserve line breaks
    const stripHtml = (html: string) => {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    };

    // Add title
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(20);
    pdf.setTextColor(30, 58, 138);
    const titleLines = pdf.splitTextToSize(pravachan.topic, pageWidth - 2 * margin);
    titleLines.forEach((line: string) => {
      if (yPosition > pdf.internal.pageSize.getHeight() - 20) {
        pdf.addPage();
        yPosition = 20;
      }
      pdf.text(line, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
    });

    // Add speaker and date
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(12);
    pdf.setTextColor(55, 65, 81);
    
    const dateObj = new Date(pravachan.dateTime || Number(timestamp));
    const formattedDate = dateObj.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    const speakerText = `By ${pravachan.vakta} • ${formattedDate}`;
    const speakerLines = pdf.splitTextToSize(speakerText, pageWidth - 2 * margin);
    
    speakerLines.forEach((line: string) => {
      if (yPosition > pdf.internal.pageSize.getHeight() - 20) {
        pdf.addPage();
        yPosition = 20;
      }
      pdf.text(line, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
    });

    yPosition += 10;

    // Add notes (with HTML stripped)
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    const cleanNotes = pravachan.notes ? stripHtml(pravachan.notes) : 'No notes recorded';
    const notesLines = pdf.splitTextToSize(cleanNotes, pageWidth - 2 * margin);
    
    notesLines.forEach((line: string) => {
      if (yPosition > pdf.internal.pageSize.getHeight() - 15) {
        pdf.addPage();
        yPosition = 20;
      }
      
      if (line.trim() === '') {
        yPosition += 7;
      } else {
        pdf.text(line, margin, yPosition);
        yPosition += 7;
      }
    });

    // Add footer on each page
    const pageCount = pdf.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(10);
      pdf.setTextColor(100);
      pdf.text(
        `Page ${i} of ${pageCount} • Shared via SwamiShreeji.com`,
        pageWidth / 2,
        pdf.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    pdf.save(`pravachan-${pravachan.topic.substring(0, 30).replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-md p-8 max-w-md text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-700 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (!pravachan) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-md p-8 max-w-md text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Pravachan Not Found</h2>
          <p className="text-gray-700 mb-6">The requested pravachan could not be found.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  const dateObj = new Date(pravachan.dateTime || Number(timestamp));
  const formattedDate = dateObj.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Preview Card (visible when link is opened) */}
      <div className="max-w-4xl mx-auto mb-8 bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
        <div className="md:flex">
          {pravachan.images?.[0] && (
            <div className="md:flex-shrink-0 md:w-1/3">
              <img 
                className="h-48 w-full object-cover md:h-full" 
                src={pravachan.images[0]} 
                alt="Discourse visual content" 
              />
            </div>
          )}
          <div className="p-8">
            <div className="uppercase tracking-wide text-sm text-indigo-500 font-semibold">
              Divine Discourse
            </div>
            <h1 className="block mt-1 text-xl leading-tight font-medium text-black">
              {pravachan.topic}
            </h1>
            <p className="mt-2 text-gray-500">
              By {pravachan.vakta} • {formattedDate}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                Spiritual Wisdom
              </span>
              <span className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
                Swaminarayan
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Full Pravachan Content */}
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4">
            <div className="text-sm text-gray-500 mb-2 md:mb-0">
              {formattedDate}
            </div>
            <div className="text-xl font-semibold text-center text-blue-700 mb-2 md:mb-0">
              {pravachan.topic}
            </div>
            <div className="text-right font-medium text-purple-700">
              {pravachan.vakta}
            </div>
          </div>
          
          {pravachan.images && pravachan.images.length > 0 && (
            <div className="my-4">
              {pravachan.images.map((imageUrl, idx) => (
                <img 
                  key={idx}
                  src={imageUrl} 
                  alt="" 
                  className="max-w-full h-auto rounded-lg shadow-sm mb-4 max-h-80 object-contain mx-auto"
                />
              ))}
            </div>
          )}
          
          <div 
            className="text-gray-700 my-4 prose max-w-none"
            dangerouslySetInnerHTML={pravachan.notes ? renderHTML(pravachan.notes) : { __html: 'No notes recorded' }}
          />

          {/* <div className="flex justify-center mt-8 pt-6 border-t border-gray-100">
            <button
              onClick={downloadPDF}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              Download as PDF
            </button>
          </div> */}
        </div>
      </div>

      <div className="mt-8 text-center">
        <p className="text-gray-600 mb-4">Want to create and share your own pravachan notes?</p>
        <button
          onClick={() => navigate('/signup')}
          className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700"
        >
          Sign Up for Free
        </button>
      </div>
    </div>
  );
}